///
/// EarthBrowser
///
/// Created by Matt Giger on 10/29/2026
/// Copyright (c) 2026 Geodata Labs
///
/// 3D marker for one satellite: the ember dome that has always marked
/// satellites (tinted by orbit regime, with a screen-space floor so it
/// stays a dot from orbit) plus a flat-shaded procedural archetype of the
/// object - station truss, comsat dish, Starlink flat-sat, debris shard -
/// drawn in the satellite's nadir / along-track frame. The archetype is a
/// single shared mesh per kind so a dense group costs one draw call per
/// visible satellite, and it drops out past a view scale so orbit views
/// keep only the dome's far-mode fleck. See satelliteArchetype.ts for how
/// an object is classified and SatelliteMarkerGeometry.ts for the meshes.
///

import { EmberDomeNode } from "@/earthbrowser/entities/EmberDome/EmberDomeNode"
import { NodeBase } from "@stovokor/engine/NodeBase"
import { type RenderActionSet, Renderer } from "@stovokor/engine/Renderer"
import { type RenderState } from "@stovokor/engine/RenderState"
import { type color, rgba } from "@stovokor/math/color"
import { matrix } from "@stovokor/math/matrix"
import { type Geometry } from "@stovokor/shader/Geometry"
import { type Material } from "@stovokor/shader/Material"
import { ShaderSourceDefinition } from "@stovokor/shader/ShaderSourceCode"
import { UniformDynamic } from "@stovokor/shader/Uniform"
import { type StovokorContext } from "@stovokor/StovokorContext"
import { type SatelliteAppearance } from "./satelliteArchetype"
import { sharedSatelliteMarkerMeshes } from "./SatelliteMarkerGeometry"

// Face-on dome solidity: a bright dot in space, quieter in far mode so
// dense groups don't flood the globe (far fill itself comes from the class)
const cDomeFill = 0.85

// While the archetype is drawn the dome recedes to a halo so the shape
// reads through it; it comes back to a solid dot as the archetype fades
const cDomeHaloOpacity = 0.25

// The archetype is lit, not glowing, so it carries full alpha and its own
// slightly brighter tint than the translucent dome
const cArchetypeBrighten = 1.15

// Archetype unit size relative to the dome radius: the meshes are modelled
// with the bus inside a unit sphere, drawn larger so wings and dishes
// clear the halo instead of hiding inside it
const cArchetypeBaseScale = 1.6

// Archetype outer reach in dome radii (the wings of the widest kinds)
const cArchetypeExtent = 1.9 * cArchetypeBaseScale

// Screen-space assist: as the camera rises the archetype is scaled up so
// the dome radius never reads under cMinMarkerPx, up to cMaxViewScale,
// fading out over the last cFadeFraction of that range. Past it only the
// dome's own far-mode fleck remains, so the shapes read at regional zoom
// without cluttering orbit views
const cMinMarkerPx = 7
const cMaxViewScale = 3
const cFadeFraction = 0.3

export class SatelliteMarkerNode extends NodeBase {
	private readonly dome: EmberDomeNode
	private readonly archetype: ArchetypeNode

	private radiusKm = 10
	private viewScale = 1
	private archetypeWanted = true
	private archetypeAlpha = 1
	private domeFadeValue = 1

	constructor(context: StovokorContext, appearance: SatelliteAppearance) {
		super()

		this.dome = new EmberDomeNode(context, appearance.color)
		this.dome.pulse = 0
		this.dome.fillAlpha = cDomeFill
		this.addNode(this.dome)

		this.archetype = new ArchetypeNode(context, sharedSatelliteMarkerMeshes(context)[appearance.archetype])
		this.addNode(this.archetype)

		this.setAppearance(appearance)
	}

	/**
	 * Colour and sizing from the classification. The archetype mesh itself
	 * is fixed at construction (a renderer's geometry is bound into its
	 * vertex array), which is fine: an object's kind never changes
	 */
	public setAppearance(appearance: SatelliteAppearance): void {
		this.radiusKm = appearance.radiusKm
		this.dome.color = appearance.color
		this.dome.farFillAlpha = appearance.farFill
		this.dome.minAngularSize = appearance.minAngular
		const c = appearance.color
		this.archetype.color = rgba(
			Math.min(1, c.r * cArchetypeBrighten),
			Math.min(1, c.g * cArchetypeBrighten),
			Math.min(1, c.b * cArchetypeBrighten),
			1
		)
		this.applyTransforms()
	}

	/** Outer reach of the marker in km at the current view scale (hit testing) */
	public get extentKm(): number {
		return this.radiusKm * (this.archetype.visible ? cArchetypeExtent : 1) * this.viewScale
	}

	/**
	 * Fade multiplier for the dome only (0..1). The archetype stays solid
	 * while a satellite is focused: it is what the user came to see
	 */
	public set domeFade(value: number) {
		if (value !== this.domeFadeValue) {
			this.domeFadeValue = value
			this.applyOpacity()
		}
	}

	/**
	 * Hide the archetype while a hero glTF model is drawn in its place, and
	 * bring it back when the model drops out with distance
	 */
	public set archetypeVisible(value: boolean) {
		if (value !== this.archetypeWanted) {
			this.archetypeWanted = value
			this.applyOpacity()
		}
	}

	/**
	 * Per-frame view update from the world size of one logical pixel at the
	 * satellite's depth: scales the archetype up as the camera rises and
	 * fades it out (while the dome returns to a solid dot) past the range
	 * where a shape could still be read
	 */
	public setWorldPerPixel(worldPerPixel: number): void {
		const needed = worldPerPixel > 0 ? (cMinMarkerPx * worldPerPixel) / this.radiusKm : 1
		const scale = Math.min(cMaxViewScale, Math.max(1, needed))
		const fadeStart = cMaxViewScale * (1 - cFadeFraction)
		const alpha = needed <= fadeStart ? 1 : Math.max(0, (cMaxViewScale - needed) / (cMaxViewScale - fadeStart))

		if (scale !== this.viewScale) {
			this.viewScale = scale
			this.applyTransforms()
		}
		if (alpha !== this.archetypeAlpha) {
			this.archetypeAlpha = alpha
			this.applyOpacity()
		}
	}

	private applyTransforms(): void {
		// The dome keeps its true size (its own shader handles the far-mode
		// floor); only the archetype scales with the view
		this.dome.transform = matrix.scale(this.radiusKm)
		this.archetype.transform = matrix.scale(this.radiusKm * cArchetypeBaseScale * this.viewScale)
	}

	private applyOpacity(): void {
		const archetype = this.archetypeWanted ? this.archetypeAlpha : 0
		this.archetype.visible = archetype > 0.002
		this.archetype.opacity = archetype
		// Halo while the shape is showing, solid dot once it has faded
		const halo = cDomeHaloOpacity + (1 - cDomeHaloOpacity) * (1 - archetype)
		this.dome.opacity = this.domeFadeValue * halo
	}

	public override render(state: RenderState): RenderActionSet[] {
		if (this.domeFadeValue <= 0.002 && !this.archetype.visible) return []
		return super.render(state)
	}
}

/** The one flat-shaded archetype solid (satellite_marker.glsl) */
class ArchetypeNode extends NodeBase {
	private readonly solidRenderer: ArchetypeRenderer

	constructor(context: StovokorContext, geometry: Geometry) {
		super()
		this.solidRenderer = new ArchetypeRenderer(context, geometry)
		this.renderer = this.solidRenderer
	}

	public set color(value: color) {
		this.solidRenderer.color.value = value
	}

	public set opacity(value: number) {
		this.solidRenderer.opacity.value = value
	}
}

class ArchetypeRenderer extends Renderer {
	public modelViewProjection = new UniformDynamic<matrix>()
	public modelView = new UniformDynamic<matrix>()
	public color = new UniformDynamic<color>(rgba(1, 1, 1, 1))
	public opacity = new UniformDynamic<number>(1)

	constructor(context: StovokorContext, geometry: Geometry) {
		// Transparent material so the solid draws in the blended pass with
		// the dome (it fades with distance), but it is a closed solid, so it
		// writes depth and culls back faces like opaque geometry: without
		// that the wings show through the bus and inner faces bleed out
		const transparentMaterial: Material = { transparency: true, available: true }
		super(context, new ShaderSourceDefinition("satellite_marker"), geometry, [transparentMaterial])
		this.drawState.depthWrite = true
		this.drawState.cullFace = true
	}

	protected stateUpdate(state: RenderState): void {
		this.modelViewProjection.value = state.modelViewProjection
		this.modelView.value = state.modelView
	}
}
